'use strict';
import classNames from 'classnames';
import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import * as T from 'prop-types';
import React from 'react';
import {I18nComponent} from '../../../core-utilities/react-i18n/I18nComponent';


export const INPUT_TYPES = {
  text: 'text',
  password: 'password',
  email: 'email',
  number: 'number',
  text_long: 'text_long',
  checkbox: 'checkbox',
  select: 'select',
};

export const ValidationMode = {
  onChange: 'onChange',
  onBlur: 'onBlur',
};

export function BaseFormFieldView(props) {
  switch (props.type) {
    case INPUT_TYPES.text:
    case INPUT_TYPES.password:
    case INPUT_TYPES.email:
    case INPUT_TYPES.number:
      return props.isEditing ? renderInputEdit(props) : renderInputView(props);

    case INPUT_TYPES.text_long:
      return props.isEditing ? renderTextareaEdit(props) : renderTextareaView(props);

    case INPUT_TYPES.checkbox:
      return props.isEditing ? renderCheckboxEdit(props) : renderCheckboxView(props);

  }
}

function renderLabel(props) {
  return (
    <label
      htmlFor={props.uid}
      className={classNames("FormField__title", props.fieldTitleClass, {
        FormField__title_required: props.isEditing && props.isRequired,
      })}
    >
      {props.isCustomCheckmark && renderCustomCheckmark(props)}
      {props.label}
      {props.isErrorAfterLabel && renderError(props)}
    </label>
  );
}

function getTextInputEditClass(props) {
  return classNames('FormField__input FormField__text',
    !props.errorValue ? 'FormField__text_valid' : 'FormField__text_error',
    {
      FormField__input_saving: props.isSaving,
    },
  );
}

function renderInputEdit(props) {
  let value;

  if (props.type === INPUT_TYPES.number) {
    value = isNumber(props.value) ? props.value : '';
  } else {
    value = props.value ? props.value : '';
  }

  return (
    <div className={classNames("FormField", props.className)}>
      {renderLabel(props)}
      <input
        id={props.uid}
        className={getTextInputEditClass(props)}
        disabled={props.disabled || props.isSaving}
        type={props.type}
        placeholder={props.placeholder}
        value={value}
        onChange={props.onChange}
        onBlur={props.onBlur}
        min={props.type === INPUT_TYPES.number ? getNumericConstraint(props.constraints, 'min') : null}
        max={props.type === INPUT_TYPES.number ? getNumericConstraint(props.constraints, 'max') : null}
        step={props.type === INPUT_TYPES.number && get(props.constraints, 'isDecimal') ? "any" : null}
      />
      {!props.isErrorAfterLabel && renderError(props)}
    </div>
  );
}

function getNumericConstraint(constraints, prop) {
  if (constraints && isNumber(constraints[prop])) {
    return constraints[prop];
  } else {
    return null;
  }
}

function renderInputView(props) {
  return (
    <div className={classNames("FormField", props.className)}>
      {renderLabel(props)}
      <div className="FormField__value">
        {props.value}
      </div>
    </div>
  );
}

function renderTextareaEdit(props) {
  const showCounter = props.constraints && isNumber(props.constraints.max);
  return (
    <div className={classNames("FormField", props.className)}>
      {renderLabel(props)}
      <textarea
        id={props.uid}
        className={getTextInputEditClass(props)}
        disabled={props.disabled || props.isSaving}
        value={props.value ? props.value : ''}
        onChange={props.onChange}
        onBlur={props.onBlur}
      />
      {showCounter ? renderMaxCounter(props) : null}
      {showCounter ? null : renderError(props)}
    </div>
  );
}

function renderMaxCounter(props) {
  const len = isString(props.value) ? props.value.length : 0;
  return (
    <div className="FormField__input_countWrapper">
      <span
        className={classNames("FormField__input_count", {
          FormField__input_count_error: !!props.error,
        })}
      >
        {`${len} / ${props.constraints.max}`}
      </span>
      {renderError(props)}
    </div>
  );
}

function renderTextareaView(props) {
  return (
    <div className={classNames("FormField", props.className)}>
      {renderLabel(props)}
      <div className="FormField__value">
        <div className="FormField__longContent">
          {props.value}
        </div>
      </div>
    </div>
  );
}

function renderCheckboxEdit(props) {
  return (
    <div className={classNames("FormField", props.className)}>
      {renderLabel(props)}
      <input
        id={props.uid}
        className={classNames("FormField__input FormField__checkbox",
          {"FormField__checkbox_hidden": props.isCustomCheckmark})}
        disabled={props.disabled || props.isSaving}
        type="checkbox"
        checked={Boolean(props.value)}
        onChange={props.onChange}
      />
    </div>
  );
}

function renderCustomCheckmark(props) {
  return (
    <span className={classNames(props.customCheckmarkClass, {
      [`${props.customCheckmarkClass}_checked`]: props.customCheckmarkClass && Boolean(props.value),
    })}
    />
  );
}

function renderCheckboxView(props) {
  return (
    <div className={classNames("FormField", props.className)}>
      {renderLabel(props)}
      <div className="FormField__value">
        {props.value ? props.trueValue : props.falseValue}
      </div>
    </div>
  );
}

function renderError(props) {
  if (props.errorValue) {
    return (
      <div className="FormField__input_error">
        {props.errorValue}
      </div>
    );
  } else {
    return null;
  }
}

BaseFormFieldView.propTypes = {
  className: T.string,
  uid: T.string.isRequired,
  type: T.oneOf(Object.keys(INPUT_TYPES)).isRequired,
  validationMode: T.oneOf(Object.keys(ValidationMode)).isRequired,
  label: T.string.isRequired,
  placeholder: T.string,
  value: T.oneOfType([
    T.string,
    T.number,
    T.bool,
  ]),
  errorValue: T.string,
  constraints: T.object,
  onChange: T.func.isRequired,
  onBlur: T.func,

  disabled: T.bool,
  isEditing: T.bool,
  isSaving: T.bool,
  isRequired: T.bool,

  // Required for checkbox type
  trueValue: T.string,
  falseValue: T.string,

  isCustomCheckmark: T.bool,
  customCheckmarkClass: T.string,
  fieldTitleClass: T.string,
  isErrorAfterLabel: T.bool,
};

BaseFormFieldView.defaultProps = {};

export const FormFieldView = I18nComponent(BaseFormFieldView, [
  {prop: 'errorValue', dynamic: getErrorMsg},
]);

function getErrorMsg(props) {
  return props.error;
}



// WEBPACK FOOTER //
// ./src/js/components/forms/FormField/FormFieldView.js